// Nonconformance reports — data access (docs/design/quality.md, PR 3).
//
//   list     rpc ncr_page(company, status, term, after_at, after_id, limit 51):
//            keyset (created_at desc, id desc) on nonconformance_reports_list_idx / _open_idx.
//   detail   rpc ncr_document(id): header, lot, locations, inspection, rework link.
//   writes   create_nonconformance / set_disposition / execute_disposition /
//            cancel_nonconformance, each with the report's revision.

import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";
import type { TFunction } from "i18next";
import type { Page } from "@/components/DataList";
import { isDemoMode, supabase } from "@/lib/supabase";
import { qualityKeys } from "./api";
import { sortParam, type ListFilter, type ListSort } from "@patronix/domain/list";

export const ncrFilters = ["open", "closed", "cancelled", "all"] as const;
export type NcrFilter = (typeof ncrFilters)[number];
export const isNcrFilter = (v: unknown): v is NcrFilter => (ncrFilters as readonly unknown[]).includes(v);
export type NcrStatus = "open" | "dispositioned" | "closed" | "cancelled";
export const dispositions = ["use_as_is", "rework", "scrap", "return_to_supplier"] as const;
export type Disposition = (typeof dispositions)[number];

export const ncrTone = { open: "danger", dispositioned: "warning", closed: "success", cancelled: "neutral" } as const;

const STALE = 30_000;
export const NCR_PAGE = 50;

export interface NcrRow {
  id: string;
  number: string;
  status: NcrStatus;
  item_id: string;
  item_code: string;
  item_description: string;
  lot_code: string | null;
  qty: string | number;
  base_uom_code: string;
  warehouse_code: string;
  location_code: string;
  disposition: Disposition | null;
  inspection_number: string | null;
  supplier_code: string | null;
  created_at: string;
}

export interface NcrDocument {
  id: string;
  number: string;
  status: NcrStatus;
  company_id: string;
  item_id: string;
  item_code: string;
  item_description: string;
  base_uom_code: string;
  base_uom_decimals: number;
  lot_id: string | null;
  lot_code: string | null;
  serial_numbers?: string[] | null;
  lot_quality_status: "released" | "in_inspection" | "rejected" | null;
  qty: string | number;
  warehouse_id: string;
  warehouse_code: string;
  location_id: string;
  location_code: string;
  target_location_id: string | null;
  target_location_code: string | null;
  supplier_code: string | null;
  supplier_name: string | null;
  inspection_order_id: string | null;
  inspection_number: string | null;
  description: string;
  disposition: Disposition | null;
  disposition_note: string | null;
  dispositioned_at: string | null;
  dispositioned_by_name: string | null;
  closed_at: string | null;
  closed_by_name: string | null;
  rework_inspection_id: string | null;
  rework_inspection_number: string | null;
  /** Return to supplier created by the disposition (ADR-054); its shipment closes the NCR. */
  purchase_return_id?: string | null;
  purchase_return_number?: string | null;
  purchase_return_status?: string | null;
  revision: number;
  created_at: string;
  created_by_name: string | null;
}

export interface NcrListParams {
  companyId: string;
  status: NcrFilter;
  q: string;
  /** Column filters and sort (list RPC spec in @patronix/domain/list). */
  filters?: ListFilter[];
  sort?: ListSort | null;
}

export const ncrKeys = {
  all: [...qualityKeys.all, "ncr"] as const,
  list: (p: NcrListParams) => [...qualityKeys.all, "ncr", "list", p] as const,
  detail: (id: string) => [...qualityKeys.all, "ncr", "detail", id] as const,
};

function db() {
  if (!supabase) throw new Error("Supabase is not configured");
  return supabase;
}
const demoOff = () => new Error("Quality is not available in demo mode");

export async function listNcrs(p: NcrListParams, cursor: string | null, signal: AbortSignal, size = NCR_PAGE): Promise<Page<NcrRow>> {
  if (isDemoMode || !p.companyId) return { rows: [], nextCursor: null };
  const { data, error } = await db()
    .rpc("ncr_page", {
      p_company_id: p.companyId,
      p_status: p.status,
      p_term: p.q.trim() || null,
      p_filters: p.filters ?? [],
      p_sort: sortParam(p.sort ?? null),
      p_after: cursor ? JSON.parse(cursor) : null,
      p_limit: size + 1,
    })
    .abortSignal(signal);
  if (error) throw error;
  const rows = data as (NcrRow & { sort_cursor: unknown })[];
  const hasMore = rows.length > size;
  const page = hasMore ? rows.slice(0, size) : rows;
  const last = page[page.length - 1];
  return { rows: page, nextCursor: hasMore && last ? JSON.stringify(last.sort_cursor) : null };
}

async function getNcr(id: string, signal: AbortSignal): Promise<NcrDocument | null> {
  if (isDemoMode) return null;
  const { data, error } = await db().rpc("ncr_document", { p_id: id }).abortSignal(signal);
  if (error) throw error;
  return (data as NcrDocument | null) ?? null;
}

export function ncrListQuery(p: NcrListParams) {
  return infiniteQueryOptions({
    queryKey: ncrKeys.list(p),
    queryFn: ({ pageParam, signal }) => listNcrs(p, pageParam, signal),
    initialPageParam: null as string | null,
    getNextPageParam: (last: Page<NcrRow>) => last.nextCursor,
    staleTime: STALE,
    enabled: !!p.companyId,
  });
}

export function ncrDetailQuery(id: string) {
  return queryOptions({ queryKey: ncrKeys.detail(id), queryFn: ({ signal }) => getNcr(id, signal), staleTime: STALE });
}

export async function createNcr(
  companyId: string,
  header: {
    item_id: string;
    warehouse_id: string;
    location_id: string;
    lot_id: string | null;
    project_id?: string | null;
    qty: string;
    description: string;
    serials?: string[];
  },
): Promise<{ id: string; number: string }> {
  if (isDemoMode) throw demoOff();
  const { data, error } = await db().rpc("create_nonconformance", { p_company_id: companyId, p_header: header });
  if (error) throw error;
  return data as { id: string; number: string };
}

export async function setDisposition(id: string, revision: number, disposition: Disposition, note: string | null, targetLocationId: string | null) {
  if (isDemoMode) throw demoOff();
  const { error } = await db().rpc("set_disposition", {
    p_id: id,
    p_revision: revision,
    p_disposition: disposition,
    p_note: note,
    p_target_location_id: targetLocationId,
  });
  if (error) throw error;
}

export async function executeDisposition(id: string, revision: number): Promise<{ rework_inspection_number?: string; purchase_return_number?: string }> {
  if (isDemoMode) throw demoOff();
  const { data, error } = await db().rpc("execute_disposition", { p_id: id, p_revision: revision });
  if (error) throw error;
  return data as { rework_inspection_number?: string; purchase_return_number?: string };
}

export async function cancelNcr(id: string, revision: number, note: string | null) {
  if (isDemoMode) throw demoOff();
  const { error } = await db().rpc("cancel_nonconformance", { p_id: id, p_revision: revision, p_note: note });
  if (error) throw error;
}

/** Dispositions that move the stock to a target location. */
export const needsTarget = (d: Disposition) => d === "use_as_is" || d === "rework";

const rules: [RegExp, string][] = [
  [/Serial .+ appears twice/, "quality.err.serialTwice"],
  [/already on an open inspection or nonconformance/, "quality.err.serialBusy"],
  [/Give the \d+ serial numbers/, "quality.err.serialCount"],
  [/Serial .+ is not in stock there/, "quality.err.serialStock"],
  [/is not serial-controlled/, "quality.err.notSerial"],
  [/changed by someone else/, "quality.err.stale"],
  [/Set the disposition first/, "quality.ncr.err.noDisposition"],
  [/Choose where the stock goes|target must be an active, receivable/, "quality.ncr.err.target"],
  [/needs a supplier/, "quality.ncr.err.supplier"],
  [/needs a disposition; it cannot be cancelled/, "quality.ncr.err.cancelFromInspection"],
  [/Rework needs an inspection plan/, "quality.ncr.err.reworkPlan"],
  [/cannot be changed/, "quality.ncr.err.final"],
  [/Describe the nonconformance/, "quality.ncr.err.description"],
  [/Choose the lot/, "quality.ins.err.lot"],
  [/is being counted/, "quality.ins.err.counted"],
  [/permission|row-level security|42501/i, "quality.ncr.err.permission"],
];

export function ncrErrorMessage(t: TFunction, error: unknown): string {
  const tr = t as unknown as (key: string, o?: Record<string, string>) => string;
  const message = (error as { message?: string } | null)?.message ?? String(error ?? "");
  const onHand = /Only ([\d.]+) on hand there/.exec(message);
  if (onHand) return tr("quality.ins.err.onHand", { qty: onHand[1] ?? "" });
  const rule = rules.find(([re]) => re.test(message));
  return rule ? tr(rule[1]) : message || tr("auth.errorGeneric");
}
